import { ViewPermissions } from '@/components/Auth/ViewPermissions';
import { FormattedMessage, useIntl } from 'react-intl';
import { useCallback, useMemo, useEffect, useState } from 'react';
import Input from '@/components/core/Input.tsx';
import { FilterPanel } from '@/components/FilterPanel';
import { OrganizationListItem } from '@/components/Organizations/OrganizationListItem';
import Button from '@/components/core/Button';
import { VirtualizedList } from '@/components/VirtualizedList';
import { RightPanel } from '@/components/RightPanel/RightPanel';
import { useAscOrDescIcon } from '@/hooks/useSort';
import { Route as OrganizationsRoute } from '@/routes/$tenantId/_layout/organizations/$view';
import { createSortConfig } from '@/utils/sort';
import { useSearchFilterSort } from '@/hooks/useSearchFilterSort';
import {
  OrganizationDTO,
  useListPayersForTenant,
  useListTenantOrganizationUsersHook,
} from '@/api/trafikportalen';
import { useExportOrganizationsToExcel } from '@/hooks/useExcelExport';
import { IconExportExcel } from '@/icons';
import { Dropdown, OptionsType } from '@/components/core/Dropdown';
import { FilterType } from '@/types/filterSearch';
import { getAllOption } from '@/helpers/filter';
import {
  getOrganizationEffectiveState,
  organizationHasStudentAccess,
} from '@/helpers/organizationStatus';
import { useListAvailableUserProfiles } from '@/api/auth';
import { useConfig, useStudentsDisabled } from '@/contexts/ConfigContext';

type OrganizationWithPayerRef = OrganizationDTO & {
  payer_reference?: string;
};

interface OrganizationFormProps {
  organizations?: OrganizationDTO[];
}

export function OrganizationsList({ organizations }: OrganizationFormProps) {
  const {
    query,
    filter: filterOrganizations,
    studentAccess: studentAccessFromUrl,
    sort,
    direction,
  } = OrganizationsRoute.useSearch();
  const getIcon = useAscOrDescIcon();
  const exportToExcel = useExportOrganizationsToExcel();
  const fetchOrganizationUsers = useListTenantOrganizationUsersHook();
  const [isExporting, setIsExporting] = useState(false);
  const studentsDisabled = useStudentsDisabled();
  const [announcement, setAnnouncement] = useState('');
  const { formatMessage } = useIntl();
  const config = useConfig();
  const { data: profiles } = useListAvailableUserProfiles({
    application_uri: config.auth.applicationUri,
  });
  const { data: payers } = useListPayersForTenant();

  const organizationsWithPayerRef = useMemo<OrganizationWithPayerRef[]>(() => {
    if (!organizations) return [];
    if (!payers) return organizations;

    const payerRefByOrgId = new Map(
      payers.map((p) => [p.organization_id, p.reference]),
    );

    return organizations.map((org) => ({
      ...org,
      payer_reference: payerRefByOrgId.get(org.id),
    }));
  }, [organizations, payers]);

  const sortConfigs = useMemo(() => {
    return createSortConfig<OrganizationWithPayerRef>({
      textFields: ['name', 'created_at'],
      numberFields: ['credit'],
    });
  }, []);

  const {
    filterSearch,
    sortedItems,
    setFilterSearch,
    handleSort,
    handleSearch,
    handleSearchParams,
  } = useSearchFilterSort<OrganizationWithPayerRef>({
    items: organizationsWithPayerRef,
    searchKeys: ['name', 'organization_number', 'payer_reference'],
    sortConfigs,
    routePath: OrganizationsRoute.fullPath,
    defaultSearchParams: {
      sort: 'name',
      direction: 'asc',
      filter: 'approved',
      studentAccess: getAllOption().id,
    },
  });

  const statusOptions = useMemo<OptionsType[]>(() => {
    const activeOption: OptionsType = {
      id: 'approved',
      label: 'Aktiv',
    };
    const inactivatingOption: OptionsType = {
      id: 'inactivating',
      label: 'Inaktiveras',
    };
    const inactiveOption: OptionsType = {
      id: 'inactive',
      label: 'Inaktiv',
    };

    return [getAllOption(), activeOption, inactivatingOption, inactiveOption];
  }, []);

  const studentAccessOptions = useMemo<OptionsType[]>(() => {
    const withAccess: OptionsType = {
      id: 'yes',
      label: formatMessage({ defaultMessage: 'Med tillgång' }),
    };
    const withoutAccess: OptionsType = {
      id: 'no',
      label: formatMessage({ defaultMessage: 'Utan tillgång' }),
    };

    return [getAllOption(), withAccess, withoutAccess];
  }, [formatMessage]);

  const filteredOrganizations = useMemo(() => {
    if (!sortedItems) {
      return [];
    }

    if (!filterSearch) {
      return sortedItems;
    }

    const statusFilterId = filterSearch.filter?.id;
    const hasStatusFilter =
      Boolean(statusFilterId) && statusFilterId !== getAllOption().id;

    const studentAccessId = filterSearch.studentAccess?.id;
    const hasStudentAccessFilter =
      !studentsDisabled &&
      Boolean(studentAccessId) &&
      studentAccessId !== getAllOption().id;

    if (!hasStatusFilter && !hasStudentAccessFilter) {
      return sortedItems;
    }

    return sortedItems.filter((organization) => {
      if (hasStatusFilter) {
        const effectiveState = getOrganizationEffectiveState(organization);
        if (effectiveState !== statusFilterId) {
          return false;
        }
      }
      if (hasStudentAccessFilter) {
        const hasStudents = organizationHasStudentAccess(organization);
        if (studentAccessId === 'yes' && !hasStudents) {
          return false;
        }
        if (studentAccessId === 'no' && hasStudents) {
          return false;
        }
      }
      return true;
    });
  }, [filterSearch, sortedItems, studentsDisabled]);

  // Announce changes to sort and filter for screen readers
  useEffect(() => {
    if (!filterSearch) return;

    const count = filteredOrganizations.length;
    const sortLabel =
      filterSearch.sort === 'name'
        ? formatMessage({ defaultMessage: 'namn' })
        : filterSearch.sort === 'credit'
          ? formatMessage({ defaultMessage: 'total kredit' })
          : formatMessage({ defaultMessage: 'skapad datum' });
    const directionLabel =
      filterSearch.direction === 'asc'
        ? formatMessage({ defaultMessage: 'stigande' })
        : formatMessage({ defaultMessage: 'fallande' });

    const message = formatMessage(
      {
        defaultMessage:
          '{count} {count, plural, one {organisation} other {organisationer}} sorterade efter {sortLabel} {directionLabel}',
      },
      { count, sortLabel, directionLabel },
    );

    setAnnouncement(message);
  }, [filterSearch, filteredOrganizations.length, formatMessage]);

  const handleExportToExcel = useCallback(async () => {
    if (!filteredOrganizations?.length) {
      return;
    }

    setIsExporting(true);
    try {
      const organizationUsers = await fetchOrganizationUsers(
        filteredOrganizations.length < 50
          ? { organization_ids: filteredOrganizations.map((org) => org.id) }
          : {},
      );
      exportToExcel(filteredOrganizations, organizationUsers);
    } catch (error) {
      console.error('Failed to fetch organization users:', error);
      exportToExcel(filteredOrganizations);
    } finally {
      setIsExporting(false);
    }
  }, [filteredOrganizations, fetchOrganizationUsers, exportToExcel]);

  const renderOrganizationListItem = useCallback(
    (organization: OrganizationWithPayerRef) => {
      return (
        <OrganizationListItem
          key={organization.id}
          organization={organization}
          profiles={profiles}
        />
      );
    },
    [profiles],
  );

  useEffect(() => {
    if (filterSearch) return;

    // Set initial filter search values
    const approvedOption =
      statusOptions.find((d) => d.id === 'approved') ?? getAllOption();
    const statusFilter = filterOrganizations
      ? (statusOptions.find((d) => d.id === filterOrganizations) ??
        approvedOption)
      : approvedOption;

    const allStudentAccessOption = getAllOption();
    const studentAccessFilter = studentAccessFromUrl
      ? (studentAccessOptions.find((d) => d.id === studentAccessFromUrl) ??
        allStudentAccessOption)
      : allStudentAccessOption;

    setFilterSearch({
      query: query ?? '',
      filter: statusFilter as FilterType,
      studentAccess: studentAccessFilter as FilterType,
      sort: sort ?? 'name',
      direction: direction ?? 'asc',
    });
  }, [
    statusOptions,
    studentAccessOptions,
    direction,
    filterOrganizations,
    studentAccessFromUrl,
    query,
    sort,
    filterSearch,
    setFilterSearch,
  ]);

  return (
    <ViewPermissions permission="tenant:viewer">
      <div aria-live="polite" aria-atomic="true" className="sr-only">
        {announcement}
      </div>
      <RightPanel>
        {filterSearch && (
          <>
            <FilterPanel
              firstInput={
                <Input
                  type="text"
                  label="Sök"
                  placeholder="Sök namn, organisationsnummer eller fakturareferens"
                  id="organization-search"
                  value={filterSearch.query}
                  onChange={(e) => handleSearch(e.target.value)}
                />
              }
              filterOptions={[
                <Dropdown
                  id="organization-status-filter"
                  label="Status"
                  placeholder="Välj status"
                  options={statusOptions}
                  value={filterSearch.filter as OptionsType}
                  onValueChange={(option: OptionsType) => {
                    setFilterSearch({
                      ...filterSearch,
                      filter: {
                        id: option.id,
                        label: String(option.label),
                      },
                    });

                    handleSearchParams({
                      filter: option.id,
                    });
                  }}
                />,
                ...(!studentsDisabled
                  ? [
                      <Dropdown
                        key="organization-student-access-filter"
                        id="organization-student-access-filter"
                        label={formatMessage({
                          defaultMessage: 'Barn & ungdomar',
                        })}
                        placeholder={formatMessage({
                          defaultMessage: 'Välj tillgång',
                        })}
                        options={studentAccessOptions}
                        value={
                          (filterSearch.studentAccess ??
                            getAllOption()) as OptionsType
                        }
                        onValueChange={(option: OptionsType) => {
                          setFilterSearch({
                            ...filterSearch,
                            studentAccess: {
                              id: option.id,
                              label: String(option.label),
                            },
                          });

                          handleSearchParams({
                            studentAccess: option.id,
                          });
                        }}
                      />,
                    ]
                  : []),
              ]}
              sortingOptions={[
                <Button
                  key="organization-name"
                  variant={filterSearch.sort! === 'name' ? 'filled' : 'outline'}
                  startIcon={getIcon({
                    selectedSort: filterSearch.sort!,
                    wantedSort: 'name',
                    sortDirection: filterSearch.direction!,
                  })}
                  onClick={() => handleSort('name')}
                  aria-label={`Sortera efter namn ${filterSearch.direction === 'asc' ? 'stigande' : 'fallande'}`}
                  aria-pressed={filterSearch.sort === 'name'}
                >
                  <FormattedMessage defaultMessage="Namn" />
                </Button>,
                <Button
                  key="organization-total-credit"
                  variant={
                    filterSearch.sort! === 'credit' ? 'filled' : 'outline'
                  }
                  startIcon={getIcon({
                    selectedSort: filterSearch.sort!,
                    wantedSort: 'credit',
                    sortDirection: filterSearch.direction!,
                  })}
                  onClick={() => handleSort('credit')}
                  aria-label={`Sortera efter total kredit ${filterSearch.direction === 'asc' ? 'stigande' : 'fallande'}`}
                  aria-pressed={filterSearch.sort === 'credit'}
                >
                  <FormattedMessage defaultMessage="Total kredit" />
                </Button>,
                <Button
                  key="organization-created-at"
                  variant={
                    filterSearch.sort! === 'created_at' ? 'filled' : 'outline'
                  }
                  startIcon={getIcon({
                    selectedSort: filterSearch.sort!,
                    wantedSort: 'created_at',
                    sortDirection: filterSearch.direction!,
                  })}
                  onClick={() => handleSort('created_at')}
                  aria-label={`Sortera efter skapad datum ${filterSearch.direction === 'asc' ? 'stigande' : 'fallande'}`}
                  aria-pressed={filterSearch.sort === 'created_at'}
                >
                  <FormattedMessage defaultMessage="Skapad datum" />
                </Button>,
              ]}
            />
            <Button
              onClick={handleExportToExcel}
              disabled={!filteredOrganizations?.length || isExporting}
              loading={isExporting}
              className="mt-2 w-full"
              startIcon={<IconExportExcel />}
            >
              <FormattedMessage defaultMessage="Exportera till Excel" />
            </Button>
          </>
        )}
      </RightPanel>
      <VirtualizedList
        count={filteredOrganizations?.length ?? 0}
        countName="organisationer"
        totalItems={organizationsWithPayerRef}
        filteredItems={filteredOrganizations ?? []}
        renderItem={renderOrganizationListItem}
        getItemKey={(organization) => organization.id}
      />
    </ViewPermissions>
  );
}
